import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api, money, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { CaretLeft, CaretRight, Island, Trash, Plus, CheckCircle, CalendarPlus, ArrowsClockwise } from "@phosphor-icons/react";
import {
  startOfMonth, endOfMonth, startOfWeek, endOfWeek, eachDayOfInterval,
  addMonths, format, isSameMonth, isToday,
} from "date-fns";
import { toast } from "sonner";
import RecurringScheduleDialog from "@/components/RecurringScheduleDialog";

const TYPES = [
  { key: "regular", label: "Regular", dot: "bg-blue-400", text: "text-blue-400" },
  { key: "paid_duty", label: "Paid Duty", dot: "bg-emerald-400", text: "text-emerald-400" },
  { key: "overtime", label: "Overtime", dot: "bg-amber-400", text: "text-amber-400" },
  { key: "court", label: "Court", dot: "bg-purple-400", text: "text-purple-400" },
  { key: "vacation", label: "Vacation", dot: "bg-slate-400", text: "text-slate-400" },
  { key: "event", label: "Event", dot: "bg-pink-400", text: "text-pink-400" },
];

// calendar bucket key -> confirm endpoint kind
const CONFIRM_KIND = { regular: "regular", paid_duty: "paid-duty", overtime: "overtime", court: "court", vacation: "vacation", event: "event" };

const SCHEDULE_TYPES = [
  { key: "regular", label: "Regular Hours" },
  { key: "paid_duty", label: "Paid Duty" },
  { key: "court", label: "Court" },
  { key: "overtime", label: "Overtime" },
  { key: "vacation", label: "Vacation" },
  { key: "event", label: "Other event" },
];

export default function CalendarPage() {
  const { user } = useAuth();
  const qc = useQueryClient();
  const currency = user?.currency || "CAD";
  const [cursor, setCursor] = useState(new Date());
  const [selected, setSelected] = useState(null);
  const [scheduleType, setScheduleType] = useState("");
  const [form, setForm] = useState({});
  const [recurringOpen, setRecurringOpen] = useState(false);

  const year = cursor.getFullYear();
  const month = cursor.getMonth() + 1;

  const { data } = useQuery({
    queryKey: ["calendar", year, month],
    queryFn: async () => (await api.get(`/calendar/${year}/${month}`)).data,
  });
  const days = data?.days || {};

  const gridStart = startOfWeek(startOfMonth(cursor), { weekStartsOn: 0 });
  const gridEnd = endOfWeek(endOfMonth(cursor), { weekStartsOn: 0 });
  const cells = eachDayOfInterval({ start: gridStart, end: gridEnd });

  const dayData = (d) => days[format(d, "yyyy-MM-dd")];
  const selectedKey = selected ? format(selected, "yyyy-MM-dd") : null;
  const selectedData = selectedKey ? days[selectedKey] : null;
  const todayKey = format(new Date(), "yyyy-MM-dd");
  const isFuture = selectedKey && selectedKey > todayKey;

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ["calendar", year, month] });
    qc.invalidateQueries({ queryKey: ["dashboard"] });
    qc.invalidateQueries({ queryKey: ["projections"] });
    ["paid-duty", "court", "overtime", "regular-hours"].forEach((k) =>
      qc.invalidateQueries({ queryKey: [k] }));
  };

  const closeDialog = () => { setSelected(null); setScheduleType(""); setForm({}); };

  const ENDPOINTS = {
    regular: "/regular-hours", paid_duty: "/paid-duty", court: "/court",
    overtime: "/overtime", vacation: "/vacation", event: "/events",
  };

  const buildPayload = (type) => {
    const base = { entry_date: selectedKey };
    if (type === "regular") return { ...base, hours: parseFloat(form.hours) || 0 };
    if (type === "overtime") return { ...base, hours: parseFloat(form.hours) || 0, multiplier: parseFloat(form.multiplier || "1.5") };
    if (type === "paid_duty") return { ...base, client: form.client || "", start_time: form.start_time || "18:00", end_time: form.end_time || "22:00", hourly_rate: parseFloat(form.hourly_rate) || 0 };
    if (type === "court") return { ...base, court_name: form.court_name || "", start_time: form.start_time || "09:00", end_time: form.end_time || "12:00", hourly_rate: parseFloat(form.hourly_rate) || 0 };
    if (type === "vacation") return { ...base, note: form.note || "" };
    if (type === "event") return { ...base, title: form.title || "", note: form.note || "" };
    return base;
  };

  const addEntry = useMutation({
    mutationFn: async () => (await api.post(ENDPOINTS[scheduleType], buildPayload(scheduleType))).data,
    onSuccess: () => {
      invalidate();
      toast.success(isFuture ? "Scheduled." : "Added.");
      setScheduleType(""); setForm({});
    },
    onError: (e) => toast.error(formatApiErrorDetail(e.response?.data?.detail)),
  });

  const confirmEntry = useMutation({
    mutationFn: async ({ kind, id }) => (await api.post(`/entries/${kind}/${id}/confirm`, {})).data,
    onSuccess: () => { invalidate(); toast.success("Confirmed as actual."); },
    onError: (e) => toast.error(formatApiErrorDetail(e.response?.data?.detail)),
  });

  const addVacation = useMutation({
    mutationFn: async (dateStr) => (await api.post("/vacation", { entry_date: dateStr, note: "" })).data,
    onSuccess: () => { invalidate(); toast.success("Marked as vacation."); },
  });
  const removeVacation = useMutation({
    mutationFn: async (id) => api.delete(`/vacation/${id}`),
    onSuccess: () => { invalidate(); toast.success("Vacation removed."); },
  });

  return (
    <div className="animate-fade-up" data-testid="calendar-page">
      <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
        <div>
          <p className="overline text-primary">Overview</p>
          <h1 className="text-3xl font-black tracking-tight mt-1">Calendar</h1>
          <p className="text-muted-foreground text-sm mt-1">Click any day to log or schedule work.</p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" data-testid="open-recurring-button" className="rounded-md font-semibold mr-1"
            onClick={() => setRecurringOpen(true)}>
            <ArrowsClockwise className="h-4 w-4 mr-1.5" weight="bold" /> Recurring
          </Button>
          <Button variant="outline" size="icon" data-testid="cal-prev" className="rounded-md"
            onClick={() => setCursor(addMonths(cursor, -1))}><CaretLeft className="h-4 w-4" weight="bold" /></Button>
          <span className="w-40 text-center font-semibold" data-testid="cal-month-label">{format(cursor, "MMMM yyyy")}</span>
          <Button variant="outline" size="icon" data-testid="cal-next" className="rounded-md"
            onClick={() => setCursor(addMonths(cursor, 1))}><CaretRight className="h-4 w-4" weight="bold" /></Button>
        </div>
      </div>

      <RecurringScheduleDialog open={recurringOpen} onOpenChange={setRecurringOpen} currency={currency} />

      <div className="flex flex-wrap gap-4 mb-4">
        {TYPES.map((t) => (
          <div key={t.key} className="flex items-center gap-2 text-xs text-muted-foreground">
            <span className={`h-2.5 w-2.5 rounded-full ${t.dot}`} />{t.label}
          </div>
        ))}
        <div className="flex items-center gap-2 text-xs text-muted-foreground">
          <span className="h-2.5 w-2.5 rounded-full ring-2 ring-muted-foreground/60 bg-transparent" /> Scheduled
        </div>
      </div>

      <Card className="bg-card border-border overflow-hidden">
        <div className="grid grid-cols-7 border-b border-border">
          {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map((d) => (
            <div key={d} className="px-2 py-2 text-center overline text-muted-foreground">{d}</div>
          ))}
        </div>
        <div className="grid grid-cols-7">
          {cells.map((d) => {
            const inMonth = isSameMonth(d, cursor);
            const dd = dayData(d);
            const key = format(d, "yyyy-MM-dd");
            return (
              <button
                key={key}
                data-testid={`cal-day-${key}`}
                onClick={() => { setSelected(d); setScheduleType(""); setForm({}); }}
                className={`min-h-[84px] border-b border-r border-border p-2 text-left transition-colors duration-200 hover:bg-secondary/40 ${
                  inMonth ? "" : "opacity-40"
                }`}
              >
                <span className={`text-sm font-semibold inline-flex items-center justify-center h-6 w-6 rounded-full ${
                  isToday(d) ? "bg-primary text-primary-foreground" : "text-foreground"
                }`}>{format(d, "d")}</span>
                <div className="flex flex-wrap gap-1 mt-1.5">
                  {dd && TYPES.map((t) =>
                    (dd[t.key] || []).map((item, i) => (
                      <span
                        key={`${t.key}-${i}`}
                        title={`${t.label}${item.status === "scheduled" ? " (scheduled)" : ""}`}
                        className={
                          item.status === "scheduled"
                            ? `h-2 w-2 rounded-full bg-transparent border-2 ${t.dot.replace("bg-", "border-")}`
                            : `h-2 w-2 rounded-full ${t.dot}`
                        }
                      />
                    ))
                  )}
                </div>
              </button>
            );
          })}
        </div>
      </Card>

      <Dialog open={!!selected} onOpenChange={(o) => !o && closeDialog()}>
        <DialogContent className="bg-popover border-border max-h-[85vh] overflow-y-auto" data-testid="cal-day-dialog">
          <DialogHeader>
            <DialogTitle>{selected && format(selected, "EEEE, MMMM d, yyyy")}</DialogTitle>
            <DialogDescription className="text-xs text-muted-foreground">
              {isFuture ? "Schedule upcoming work or review planned entries." : "Log work or review entries for this day."}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            {/* Existing entries */}
            {(() => {
              const hasAny = selectedData && TYPES.some((t) => selectedData[t.key]?.length);
              if (!hasAny) return <p className="text-sm text-muted-foreground" data-testid="cal-day-empty">Nothing on this day yet.</p>;
              return (
                <div className="space-y-2">
                  {TYPES.map((t) =>
                    (selectedData[t.key] || []).map((item) => (
                      <div key={item.id} data-testid={`cal-entry-${item.id}`}
                        className="flex items-center justify-between gap-3 p-3 rounded-md bg-secondary/40">
                        <div className="flex items-center gap-2 min-w-0">
                          <span className={`h-2.5 w-2.5 rounded-full shrink-0 ${item.status === "scheduled" ? `ring-2 ${t.dot.replace("bg-", "ring-")} bg-transparent` : t.dot}`} />
                          <div className="min-w-0">
                            <p className="text-sm font-medium truncate flex items-center gap-2">
                              {item.label}
                              {item.status === "scheduled" && (
                                <span className="text-[10px] uppercase tracking-wide px-1.5 py-0.5 rounded bg-muted-foreground/20 text-muted-foreground" data-testid={`scheduled-badge-${item.id}`}>Scheduled</span>
                              )}
                            </p>
                            <p className="text-xs text-muted-foreground">
                              {t.label}{item.hours != null ? ` · ${item.hours}h` : ""}
                              {item.start_time ? ` · ${item.start_time}–${item.end_time}` : ""}
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center gap-2 shrink-0">
                          {item.amount != null && <span className={`font-bold ${t.text}`}>{money(item.amount, currency)}</span>}
                          {item.status === "scheduled" && CONFIRM_KIND[t.key] && (
                            <Button size="sm" variant="outline" data-testid={`confirm-entry-${item.id}`}
                              disabled={confirmEntry.isPending}
                              onClick={() => confirmEntry.mutate({ kind: CONFIRM_KIND[t.key], id: item.id })}
                              className="rounded-md h-7 px-2 text-xs">
                              <CheckCircle className="h-3.5 w-3.5 mr-1" weight="bold" /> Confirm
                            </Button>
                          )}
                        </div>
                      </div>
                    ))
                  )}
                </div>
              );
            })()}

            {/* Schedule / add */}
            <div className="pt-3 border-t border-border space-y-3">
              <div className="flex items-center gap-2 text-sm font-semibold">
                <CalendarPlus className="h-4 w-4 text-primary" weight="bold" />
                {isFuture ? "Schedule upcoming work" : "Add work"}
              </div>
              <Select value={scheduleType} onValueChange={(v) => { setScheduleType(v); setForm({}); }}>
                <SelectTrigger className="bg-secondary/50" data-testid="schedule-type-select">
                  <SelectValue placeholder="Choose type…" />
                </SelectTrigger>
                <SelectContent>
                  {SCHEDULE_TYPES.map((t) => <SelectItem key={t.key} value={t.key}>{t.label}</SelectItem>)}
                </SelectContent>
              </Select>

              {scheduleType && (
                <form onSubmit={(e) => { e.preventDefault(); addEntry.mutate(); }} className="space-y-3" data-testid="schedule-form">
                  {scheduleType === "paid_duty" && (
                    <Input placeholder="Client" data-testid="sched-client" className="bg-secondary/50" required
                      value={form.client || ""} onChange={(e) => setForm({ ...form, client: e.target.value })} />
                  )}
                  {scheduleType === "court" && (
                    <Input placeholder="Court name" data-testid="sched-court-name" className="bg-secondary/50" required
                      value={form.court_name || ""} onChange={(e) => setForm({ ...form, court_name: e.target.value })} />
                  )}
                  {scheduleType === "event" && (
                    <Input placeholder="Event title (e.g. Training day)" data-testid="sched-event-title" className="bg-secondary/50" required
                      value={form.title || ""} onChange={(e) => setForm({ ...form, title: e.target.value })} />
                  )}
                  {(scheduleType === "paid_duty" || scheduleType === "court") && (
                    <>
                      <div className="grid grid-cols-2 gap-2">
                        <div><Label className="text-xs">Start</Label>
                          <Input type="time" data-testid="sched-start" className="bg-secondary/50 mt-1"
                            value={form.start_time || (scheduleType === "court" ? "09:00" : "18:00")}
                            onChange={(e) => setForm({ ...form, start_time: e.target.value })} /></div>
                        <div><Label className="text-xs">End</Label>
                          <Input type="time" data-testid="sched-end" className="bg-secondary/50 mt-1"
                            value={form.end_time || (scheduleType === "court" ? "12:00" : "22:00")}
                            onChange={(e) => setForm({ ...form, end_time: e.target.value })} /></div>
                      </div>
                      <Input type="number" step="0.01" min="0" placeholder={`Hourly rate (${currency})`} data-testid="sched-rate"
                        className="bg-secondary/50" required value={form.hourly_rate || ""}
                        onChange={(e) => setForm({ ...form, hourly_rate: e.target.value })} />
                    </>
                  )}
                  {(scheduleType === "regular" || scheduleType === "overtime") && (
                    <Input type="number" step="0.25" min="0" placeholder="Hours" data-testid="sched-hours"
                      className="bg-secondary/50" required value={form.hours || ""}
                      onChange={(e) => setForm({ ...form, hours: e.target.value })} />
                  )}
                  {scheduleType === "overtime" && (
                    <Select value={form.multiplier || "1.5"} onValueChange={(v) => setForm({ ...form, multiplier: v })}>
                      <SelectTrigger className="bg-secondary/50" data-testid="sched-multiplier"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="1.5">1.5× (Time and a half)</SelectItem>
                        <SelectItem value="2">2× (Double time)</SelectItem>
                      </SelectContent>
                    </Select>
                  )}
                  {scheduleType === "event" && (
                    <Input placeholder="Note (optional)" data-testid="sched-note" className="bg-secondary/50"
                      value={form.note || ""} onChange={(e) => setForm({ ...form, note: e.target.value })} />
                  )}
                  {scheduleType === "vacation" && (
                    <Input placeholder="Note (optional)" data-testid="sched-note" className="bg-secondary/50"
                      value={form.note || ""} onChange={(e) => setForm({ ...form, note: e.target.value })} />
                  )}
                  <Button type="submit" disabled={addEntry.isPending} data-testid="schedule-submit" className="w-full rounded-md font-semibold">
                    <Plus className="h-4 w-4 mr-1.5" weight="bold" />
                    {addEntry.isPending ? "Saving…" : isFuture ? "Schedule" : "Add"}
                  </Button>
                </form>
              )}
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
